import * as moment from 'moment'

import processTransactions from './processTransactions'
import { LoanQueryResult } from '../../components/queries/LoanQuery'
import {
  initialState,
  computeState,
  LoanType,
  sortTransactions,
  PaymentTransaction
} from '.'
import { toNumber as intervalTypeToNumber } from '../../constants/IntervalTypes'
import { moneyRound } from '../format'

type Loan = Exclude<LoanQueryResult['loan'], null>

const dateDiff = (a: moment.Moment, b: moment.Moment, interval: number) =>
  Math.floor(a.diff(b, 'days') / interval)

export const calculateRequiredPayment = (loan: Loan, date: moment.Moment) => {
  const transactions = processTransactions(loan, date)
  const startedOn = moment(loan.startedOn)
  const dateToUse = moment.min([
    loan.completedOn != null ? moment(loan.completedOn) : null,
    date,
  ].filter((item): item is moment.Moment => item != null))

  const latestTransaction = transactions[transactions.length - 1]
  const firstTransaction = transactions[0]
  const latestDate = moment.max([latestTransaction.createdOn, dateToUse])

  if (firstTransaction.type !== 'INIT') {
    throw new Error()
  }

  const interest = firstTransaction.interest / 100

  if (loan.interval != null) {
    const interval = intervalTypeToNumber[loan.interval]
    const biggestIntervalDifference = dateDiff(latestDate, startedOn, interval)
    const dateIntervalDifference = dateDiff(date, startedOn, interval)
    const correctInterest = interest / 365 * intervalTypeToNumber[loan.interval]

    let total = moneyRound(
      firstTransaction.amount *
        Math.pow(1 + correctInterest, biggestIntervalDifference)
    )

    for (let i = 0; i < transactions.length; i++) {
      const tr = transactions[i]

      if (tr.type !== 'PAYMENT') {
        continue
      }

      total = moneyRound(
        total -
          tr.amount *
            Math.pow(
              1 + correctInterest,
              biggestIntervalDifference -
                dateDiff(tr.createdOn, startedOn, interval)
            )
      )
    }

    const calculatedTotal = moneyRound(
      total /
        Math.pow(
          1 + correctInterest,
          biggestIntervalDifference - dateIntervalDifference
        )
    )

    // calculated total is in this case not affected by flooring function
    // we will correct it by computing current state and add resulting difference
    const tmpTransaction: PaymentTransaction = {
      id: 'tmp',
      type: 'PAYMENT',
      createdOn: date,
      amount: calculatedTotal
    }
    const correctTransactions = sortTransactions([
      ...processTransactions(loan, latestDate),
      tmpTransaction
    ])
    const computeDiff = () =>
      correctTransactions.reduce(
        (res, it) => computeState(LoanType.INTERVAL, it, res),
        initialState
      ).amount

    let currDiff = computeDiff()
    let steps = 0

    // usually one step is enough (the lost in precision is really small)
    // after the first step the lost in precision should be < 1 (dependent on number of transactions)
    while (currDiff !== 0 && steps < 7) {
      tmpTransaction.amount = moneyRound(tmpTransaction.amount + currDiff)
      currDiff = computeDiff()
      steps++
    }

    return moneyRound(tmpTransaction.amount)
  }

  const totalPayed = transactions.reduce(
    (res, it) => {
      if (it.type === 'PAYMENT') {
        return res + it.amount
      }

      return res
    },
    0
  )

  const dateDifference = latestDate.diff(startedOn, 'days')

  return moneyRound(firstTransaction.amount * (1 + dateDifference * interest / 365) - totalPayed)
}



// WEBPACK FOOTER //
// ./src/utils/transactions/calculateRequiredPayment.tsx